/**
 * Static Centralized configuration for user limits
 * This file contains all the limits for different subscription plans
 *
 * CURRENT SETUP: Using static configuration (no API calls)
 * - BACKEND_PLAN_LIMITS: Matches your backend PLAN_LIMITS exactly
 * - KB_LIMITS: Additional Knowledge Base limits (frontend-only)
 * - Combined into DEFAULT_PLAN_LIMITS for use throughout the app
 *
 * When you're ready to use API calls, you can re-enable the LimitsService functions.
 */

// Static plan limits configuration - matches backend PLAN_LIMITS exactly
const BACKEND_PLAN_LIMITS = {
  "Free": {
    "MAX_CALLS_PER_USER": 10,
    "MAX_CALL_DURATION_SECONDS": 60,
    "MAX_AGENT_CREATION_LIMIT": 2,
    "MAX_AGENT_CHAT_LIMIT": 2,
    "MAX_PLAYGROUND_CHAT_LIMIT": 1,
    "MAX_QUESTIONS_FOR_AGENT_CHAT": 2,
    "MAX_QUESTIONS_FOR_PLAYGROUND_CHAT": 2,
    "MAX_DOCUMENTS_UPLOAD_LIMIT": 2,
    "MAX_AGENT_UPDATE_LIMIT": 2,
  },
  "Starter": {
    "MAX_CALLS_PER_USER": 20,
    "MAX_CALL_DURATION_SECONDS": 120,
    "MAX_AGENT_CREATION_LIMIT": 4,
    "MAX_AGENT_CHAT_LIMIT": 4,
    "MAX_PLAYGROUND_CHAT_LIMIT": 4,
    "MAX_QUESTIONS_FOR_AGENT_CHAT": 10,
    "MAX_QUESTIONS_FOR_PLAYGROUND_CHAT": 10,
    "MAX_DOCUMENTS_UPLOAD_LIMIT": 4,
    "MAX_AGENT_UPDATE_LIMIT": 4,
  },
  "Professional": {
    "MAX_CALLS_PER_USER": 30,
    "MAX_CALL_DURATION_SECONDS": 180,
    "MAX_AGENT_CREATION_LIMIT": 6,
    "MAX_AGENT_CHAT_LIMIT": 6,
    "MAX_PLAYGROUND_CHAT_LIMIT": 6,
    "MAX_QUESTIONS_FOR_AGENT_CHAT": 15,
    "MAX_QUESTIONS_FOR_PLAYGROUND_CHAT": 15,
    "MAX_DOCUMENTS_UPLOAD_LIMIT": 6,
    "MAX_AGENT_UPDATE_LIMIT": 6,
  },
  "Enterprise": {
    "MAX_CALLS_PER_USER": 40,
    "MAX_CALL_DURATION_SECONDS": 240,
    "MAX_AGENT_CREATION_LIMIT": 8,
    "MAX_AGENT_CHAT_LIMIT": 8,
    "MAX_PLAYGROUND_CHAT_LIMIT": 8,
    "MAX_QUESTIONS_FOR_AGENT_CHAT": 20,
    "MAX_QUESTIONS_FOR_PLAYGROUND_CHAT": 20,
    "MAX_DOCUMENTS_UPLOAD_LIMIT": 8,
    "MAX_AGENT_UPDATE_LIMIT": 8,
  },
};

// Additional KB limits (frontend-only for now)
// Note: KB uploads use the same server limits as document uploads
const KB_LIMITS = {
  "Free": {
    "MAX_KB_UPLOAD_LIMIT": 2, // Matches server document limit
    "MAX_KB_FILE_SIZE_MB": 10,
    "MAX_KB_CONTEXTS": 2,
  },
  "Starter": {
    "MAX_KB_UPLOAD_LIMIT": 4, // Matches server document limit
    "MAX_KB_FILE_SIZE_MB": 25,
    "MAX_KB_CONTEXTS": 5,
  },
  "Professional": {
    "MAX_KB_UPLOAD_LIMIT": 6, // Matches server document limit
    "MAX_KB_FILE_SIZE_MB": 50,
    "MAX_KB_CONTEXTS": 15,
  },
  "Enterprise": {
    "MAX_KB_UPLOAD_LIMIT": 8, // Matches server document limit
    "MAX_KB_FILE_SIZE_MB": 100,
    "MAX_KB_CONTEXTS": -1, // Unlimited
  },
};

// Combined limits (backend + KB limits)
const DEFAULT_PLAN_LIMITS = {};
Object.keys(BACKEND_PLAN_LIMITS).forEach(plan => {
  DEFAULT_PLAN_LIMITS[plan] = {
    ...BACKEND_PLAN_LIMITS[plan],
    ...KB_LIMITS[plan]
  };
});

// Current active limits - can be updated dynamically
let CURRENT_PLAN_LIMITS = { ...DEFAULT_PLAN_LIMITS };

// Usage tracking data storage
let USAGE_COUNTS = {
  call_count: 0,
  agent_count: 0,
  agent_chat_count: 0,
  playground_chat_count: 0,
  document_count: 0,
  user_count: 0,
  chat_agent_count: 0,
  chat_agent_chat_count: 0,
  agent_update_count: 0
};

// Legacy format for backward compatibility
export const USER_LIMITS = {
  FREE: {
    AGENT: {
      get MAX_COUNT() { return CURRENT_PLAN_LIMITS.Free?.MAX_AGENT_CREATION_LIMIT || 2; },
      get ERROR_MESSAGE() { return getDynamicErrorMessage('Free', 'AGENT_CREATION_LIMIT'); }
    },
    CHAT: {
      get MAX_MESSAGES() { return CURRENT_PLAN_LIMITS.Free?.MAX_QUESTIONS_FOR_AGENT_CHAT || 2; },
      get ERROR_MESSAGE() { return `For current chat message limit reached. You can only have ${this.MAX_MESSAGES} messages.`; }
    },
    PLAYGROUND: {
      get MAX_CONVERSATIONS() { return CURRENT_PLAN_LIMITS.Free?.MAX_PLAYGROUND_CHAT_LIMIT || 1; },
      get MAX_MESSAGES() { return CURRENT_PLAN_LIMITS.Free?.MAX_QUESTIONS_FOR_PLAYGROUND_CHAT || 2; },
      get ERROR_MESSAGE() { return `Conversation limit reached. You can only have ${this.MAX_CONVERSATIONS} conversation${this.MAX_CONVERSATIONS > 1 ? 's' : ''}.`; }
    },
    DOCUMENT: {
      get MAX_COUNT() { return CURRENT_PLAN_LIMITS.Free?.MAX_DOCUMENTS_UPLOAD_LIMIT || 2; },
      get ERROR_MESSAGE() { return `Document upload limit exceeded for free users. Max allowed: ${this.MAX_COUNT}.`; }
    }
  },
  // Note: PREMIUM is deprecated - use specific subscription types instead
  PREMIUM: {
    AGENT: {
      get MAX_COUNT() { return CURRENT_PLAN_LIMITS.Professional?.MAX_AGENT_CREATION_LIMIT || 6; },
      get ERROR_MESSAGE() { return getDynamicErrorMessage('Professional', 'AGENT_CREATION_LIMIT'); }
    },
    CHAT: {
      get MAX_MESSAGES() { return CURRENT_PLAN_LIMITS.Professional?.MAX_QUESTIONS_FOR_AGENT_CHAT || 15; },
      get ERROR_MESSAGE() { return `For current chat message limit reached. You can only have ${this.MAX_MESSAGES} messages.`; }
    },
    PLAYGROUND: {
      get MAX_CONVERSATIONS() { return CURRENT_PLAN_LIMITS.Professional?.MAX_PLAYGROUND_CHAT_LIMIT || 6; },
      get MAX_MESSAGES() { return CURRENT_PLAN_LIMITS.Professional?.MAX_QUESTIONS_FOR_PLAYGROUND_CHAT || 15; },
      get ERROR_MESSAGE() { return `Conversation limit reached. You can only have ${this.MAX_CONVERSATIONS} conversation${this.MAX_CONVERSATIONS > 1 ? 's' : ''}.`; }
    },
    DOCUMENT: {
      get MAX_COUNT() { return CURRENT_PLAN_LIMITS.Professional?.MAX_DOCUMENTS_UPLOAD_LIMIT || 6; },
      get ERROR_MESSAGE() { return `Document upload limit exceeded for premium users. Max allowed: ${this.MAX_COUNT}.`; }
    }
  },
  // New: Specific subscription type limits
  STARTER: {
    AGENT: {
      get MAX_COUNT() { return CURRENT_PLAN_LIMITS.Starter?.MAX_AGENT_CREATION_LIMIT || 4; },
      get ERROR_MESSAGE() { return getDynamicErrorMessage('Starter', 'AGENT_CREATION_LIMIT'); }
    }
  },
  PROFESSIONAL: {
    AGENT: {
      get MAX_COUNT() { return CURRENT_PLAN_LIMITS.Professional?.MAX_AGENT_CREATION_LIMIT || 6; },
      get ERROR_MESSAGE() { return getDynamicErrorMessage('Professional', 'AGENT_CREATION_LIMIT'); }
    }
  },
  ENTERPRISE: {
    AGENT: {
      get MAX_COUNT() { return CURRENT_PLAN_LIMITS.Enterprise?.MAX_AGENT_CREATION_LIMIT || 8; },
      get ERROR_MESSAGE() { return getDynamicErrorMessage('Enterprise', 'AGENT_CREATION_LIMIT'); }
    }
  }
};

/**
 * Get the current plan limits for a specific subscription type
 * @param {string} subscriptionType - The subscription type (Free, Starter, Professional, Enterprise)
 * @returns {Object} The limits for the subscription type
 */
export const getPlanLimits = (subscriptionType) => {
  return CURRENT_PLAN_LIMITS[subscriptionType] || CURRENT_PLAN_LIMITS.Free;
};

/**
 * Get the appropriate limits based on subscription type (legacy compatibility)
 * @param {boolean} isPremium - Whether the user has a premium subscription
 * @returns {Object} The limits for the user's subscription type
 */
export const getLimits = (isPremium) => {
  return isPremium ? USER_LIMITS.PREMIUM : USER_LIMITS.FREE;
};

/**
 * Get limits based on exact subscription type
 * @param {string} subscriptionType - The exact subscription type
 * @returns {Object} The limits for the subscription type
 */
export const getLimitsBySubscription = (subscriptionType) => {
  // If no subscription type is provided, assume Free
  if (!subscriptionType) {
    return getPlanLimits('Free');
  }

  // Return limits based on subscription type only
  return getPlanLimits(subscriptionType);
};

/**
 * Update the current plan limits dynamically
 * @param {Object} newLimits - New limits configuration
 */
export const updatePlanLimits = (newLimits) => {
  CURRENT_PLAN_LIMITS = { ...CURRENT_PLAN_LIMITS, ...newLimits };

  // Trigger a custom event to notify components of the update
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('planLimitsUpdated', {
      detail: { limits: CURRENT_PLAN_LIMITS }
    }));
  }
};

/**
 * Reset plan limits to default values
 */
export const resetPlanLimits = () => {
  CURRENT_PLAN_LIMITS = { ...DEFAULT_PLAN_LIMITS };

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('planLimitsUpdated', {
      detail: { limits: CURRENT_PLAN_LIMITS }
    }));
  }
};

/**
 * Update plan limits with usage data from API
 * @param {string} subscriptionType - The subscription type
 * @param {Object} apiLimits - Limits from API (optional)
 * @param {Object} usageCounts - Usage counts from API
 */
export const updatePlanLimitsWithUsage = (subscriptionType, apiLimits = {}, usageCounts = {}) => {
  // Update usage counts
  USAGE_COUNTS = { ...USAGE_COUNTS, ...usageCounts };

  // If API provides limits, merge them with existing limits for the subscription type
  if (apiLimits && Object.keys(apiLimits).length > 0) {
    const currentLimits = CURRENT_PLAN_LIMITS[subscriptionType] || {};
    CURRENT_PLAN_LIMITS[subscriptionType] = {
      ...currentLimits,
      ...apiLimits
    };
  }

  // Trigger update event
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('planLimitsUpdated', {
      detail: {
        limits: CURRENT_PLAN_LIMITS,
        usageCounts: USAGE_COUNTS,
        subscriptionType
      }
    }));
  }
};

/**
 * Get current usage counts
 * @returns {Object} Current usage counts
 */
export const getUsageCounts = () => {
  return { ...USAGE_COUNTS };
};

/**
 * Get usage count for a specific metric
 * @param {string} metric - Metric name (e.g., 'agent_count', 'document_count')
 * @returns {number} Usage count or 0 if not available
 */
export const getUsageCount = (metric) => {
  return USAGE_COUNTS[metric] || 0;
};

/**
 * Update a specific usage count
 * @param {string} metric - Metric name
 * @param {number} count - New count value
 */
export const updateUsageCount = (metric, count) => {
  if (typeof count === 'number' && !isNaN(count)) {
    USAGE_COUNTS[metric] = count;

    // Trigger update event
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('usageCountUpdated', {
        detail: {
          metric,
          count,
          usageCounts: USAGE_COUNTS
        }
      }));
    }
  }
};

/**
 * Check if usage is within limit for a specific metric
 * @param {string} subscriptionType - User's subscription type
 * @param {string} limitType - Type of limit to check
 * @param {number} currentUsage - Current usage (optional, will use stored count if not provided)
 * @returns {Object} Object with withinLimit boolean and usage info
 */
export const checkUsageLimit = (subscriptionType, limitType, currentUsage = null) => {
  const limits = getPlanLimits(subscriptionType);
  const limitKey = `MAX_${limitType}`;
  const maxLimit = limits[limitKey];

  // Determine current usage
  let usage = currentUsage;
  if (usage === null) {
    // Map limit types to usage count keys
    const usageKeyMap = {
      'AGENT_CREATION_LIMIT': 'agent_count',
      'DOCUMENTS_UPLOAD_LIMIT': 'document_count',
      'AGENT_CHAT_LIMIT': 'agent_chat_count',
      'PLAYGROUND_CHAT_LIMIT': 'playground_chat_count',
      'CALLS_PER_USER': 'call_count',
      'USER_CREATION_LIMIT': 'user_count',
      'AGENT_UPDATE_LIMIT': 'agent_update_count'
    };

    const usageKey = usageKeyMap[limitType];
    usage = usageKey ? getUsageCount(usageKey) : 0;
  }

  const withinLimit = maxLimit === -1 || usage < maxLimit; // -1 means unlimited

  return {
    withinLimit,
    currentUsage: usage,
    maxLimit,
    remaining: maxLimit === -1 ? Infinity : Math.max(0, maxLimit - usage),
    usagePercentage: maxLimit === -1 ? 0 : Math.min(100, (usage / maxLimit) * 100)
  };
};

/**
 * Get a dynamic error message for a specific limit
 * @param {string} subscriptionType - The subscription type
 * @param {string} limitType - The type of limit (AGENT_CREATION, DOCUMENTS_UPLOAD, etc.)
 * @returns {string} Dynamic error message
 */
export const getDynamicErrorMessage = (subscriptionType, limitType) => {
  // Handle undefined or null limitType
  if (!limitType) {
    return "You've reached a usage limit.";
  }

  const limits = getPlanLimits(subscriptionType);
  const limitKey = `MAX_${limitType}`;
  const limitValue = limits[limitKey];

  const messages = {
    'AGENT_CREATION_LIMIT': `${subscriptionType} subscription limit reached. You already have ${limitValue} agents.`,
    'DOCUMENTS_UPLOAD_LIMIT': `Document upload limit exceeded for ${subscriptionType} users. Max allowed: ${limitValue}.`,
    'QUESTIONS_FOR_AGENT_CHAT': `Agent chat message limit reached. You can only have ${limitValue} messages per chat session.${subscriptionType === 'Free' ? ' Upgrade to get higher limits.' : ''}`,
    'QUESTIONS_FOR_PLAYGROUND_CHAT': `Search message limit reached. You can only have ${limitValue} messages per search session.${subscriptionType === 'Free' ? ' Upgrade to get higher limits.' : ''}`,
    'PLAYGROUND_CHAT_LIMIT': `Search conversation limit reached. You can only have ${limitValue} conversation${limitValue > 1 ? 's' : ''}.${subscriptionType === 'Free' ? ' Upgrade to get higher limits.' : ''}`,
    'CALLS_PER_USER': `Call limit reached. You can only make ${limitValue} calls per day.${subscriptionType === 'Free' ? ' Upgrade to get higher limits.' : ''}`,
    'CALL_DURATION_SECONDS': `Call duration limit reached. Maximum duration is ${Math.floor(limitValue / 60)} minutes per call.${subscriptionType === 'Free' ? ' Upgrade to get longer call duration.' : ''}`,
    'AGENT_CHAT_LIMIT': `Agent chat limit reached. You can only have ${limitValue} active chat session${limitValue > 1 ? 's' : ''}.${subscriptionType === 'Free' ? ' Upgrade to get higher limits.' : ''}`,
    'AGENT_UPDATE_LIMIT': `Agent update limit reached. You can only update ${limitValue} agents.`,
    'KB_UPLOAD_LIMIT': limitValue === -1 ? 'Unlimited knowledge base uploads available.' : `Knowledge base upload limit reached. You can only upload ${limitValue} files to knowledge base.`,
    'KB_FILE_SIZE_MB': `File size exceeds the ${limitValue}MB limit for ${subscriptionType} users.`,
    'KB_CONTEXTS': limitValue === -1 ? 'Unlimited knowledge base contexts available.' : `Knowledge base context limit reached. You can only create ${limitValue} context${limitValue > 1 ? 's' : ''}.`,
    'GENERIC': `You've reached a usage limit for your ${subscriptionType} subscription.${subscriptionType === 'Free' ? ' Upgrade to get higher limits.' : ' Please contact support if you need assistance.'}`,
  };

  // Create user-friendly fallback message
  const createFallbackMessage = (limitType, subscriptionType) => {
    const friendlyNames = {
      'AGENT_CREATION_LIMIT': 'agent creation',
      'DOCUMENTS_UPLOAD_LIMIT': 'document upload',
      'QUESTIONS_FOR_AGENT_CHAT': 'agent chat message',
      'QUESTIONS_FOR_PLAYGROUND_CHAT': 'search message',
      'PLAYGROUND_CHAT_LIMIT': 'search conversation',
      'CALLS_PER_USER': 'daily call',
      'CALL_DURATION_SECONDS': 'call duration',
      'AGENT_CHAT_LIMIT': 'agent chat',
      'AGENT_UPDATE_LIMIT': 'agent update',
      'KB_UPLOAD_LIMIT': 'knowledge base upload',
      'KB_FILE_SIZE_MB': 'file size',
      'KB_CONTEXTS': 'knowledge base context',
      'GENERIC': 'usage'
    };

    const friendlyName = friendlyNames[limitType] || 'usage';
    return `You've reached the ${friendlyName} limit for your ${subscriptionType} subscription.${subscriptionType === 'Free' ? ' Upgrade to get higher limits.' : ''}`;
  };

  return messages[limitType] || createFallbackMessage(limitType, subscriptionType);
};

/**
 * Get the appropriate limit message for a specific feature (legacy compatibility)
 * @param {boolean} isPremium - Whether the user has a premium subscription
 * @param {string} feature - The feature to get the limit message for (AGENT, CHAT, PLAYGROUND, DOCUMENT)
 * @returns {string} The limit message for the feature
 */
export const getLimitMessage = (isPremium, feature) => {
  const limits = getLimits(isPremium);
  return limits[feature]?.ERROR_MESSAGE || "You've reached a usage limit.";
};

/**
 * Check if an error is a limit reached error
 * @param {Object} error - The error object from the API
 * @param {boolean} isPremium - Whether the user has a premium subscription
 * @returns {Object} Object containing isLimitError and limitType
 */
// Helper function to check if error message includes any of the given patterns
const checkErrorPatterns = (errorLower, patterns) => {
  return patterns.some(pattern => errorLower.includes(pattern));
};

export const checkLimitError = (error, isPremium) => {
  if (!error) return { isLimitError: false, limitType: null };

  const errorDetail = error?.response?.data?.detail || error?.data?.detail || '';
  const errorLower = typeof errorDetail === 'string' ? errorDetail.toLowerCase() : '';
  const limits = getLimits(isPremium);

  // Check for specific limit error messages
  if (typeof errorDetail === 'string' && errorDetail.includes(limits.AGENT.ERROR_MESSAGE)) {
    return { isLimitError: true, limitType: 'AGENT' };
  }

  if (typeof errorDetail === 'string' && errorDetail.includes(limits.CHAT.ERROR_MESSAGE)) {
    return { isLimitError: true, limitType: 'CHAT' };
  }

  if (typeof errorDetail === 'string' && errorDetail.includes(limits.PLAYGROUND.ERROR_MESSAGE)) {
    return { isLimitError: true, limitType: 'PLAYGROUND' };
  }

  if (typeof errorDetail === 'string' && errorDetail.includes(limits.DOCUMENT.ERROR_MESSAGE)) {
    return { isLimitError: true, limitType: 'DOCUMENT' };
  }

  // Enhanced chat limit detection
  if (checkErrorPatterns(errorLower, ['chat message limit', 'message limit reached', 'chat limit reached']) ||
      (errorLower.includes('limit reached') && errorLower.includes('message'))) {
    return { isLimitError: true, limitType: 'CHAT' };
  }

  // Enhanced agent limit detection
  if (checkErrorPatterns(errorLower, ['agent limit', 'agent creation limit'])) {
    return { isLimitError: true, limitType: 'AGENT' };
  }

  // Enhanced playground/search limit detection
  if (checkErrorPatterns(errorLower, ['playground limit', 'search limit', 'conversation limit'])) {
    return { isLimitError: true, limitType: 'PLAYGROUND' };
  }

  // Enhanced document limit detection
  if (checkErrorPatterns(errorLower, ['document limit', 'upload limit', 'document upload'])) {
    return { isLimitError: true, limitType: 'DOCUMENT' };
  }

  // Generic check for "limit reached" or "limit exceeded"
  if (checkErrorPatterns(errorLower, ['limit reached', 'limit exceeded'])) {
    return { isLimitError: true, limitType: 'GENERIC' };
  }

  return { isLimitError: false, limitType: null };
};
